import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Mail, Heart, Palette } from "lucide-react";
import { api, API } from "@/lib/api";

const initials = (name) =>
  (name || "").split(" ").filter(Boolean).slice(0, 2).map((n) => n[0]).join("").toUpperCase();

const EquipoDetail = () => {
  const { slug } = useParams();
  const [m, setM] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get("/team").then((r) => {
      setM(r.data.find((x) => x.slug === slug) || null);
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, [slug]);

  if (!loaded) return <div className="max-w-5xl mx-auto px-6 py-20 text-brand-ink/40">Cargando…</div>;
  if (!m) return <div className="max-w-5xl mx-auto px-6 py-20 text-brand-ink/60">Perfil no encontrado.</div>;

  const photo = m.photo_ref ? (m.photo_ref.startsWith("/") ? m.photo_ref : `${API}/media/${m.photo_ref}`) : null;

  return (
    <div data-testid={`equipo-${m.slug}`} className="max-w-5xl mx-auto px-6 md:px-12 py-16">
      <Link to="/conocenos/nuestros-consejos" data-testid="equipo-back" className="inline-flex items-center gap-2 text-sm font-semibold text-brand-ink/60 hover:text-brand-ink mb-8">
        <ArrowLeft size={16} /> Nuestros consejos
      </Link>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="md:col-span-5">
          <div className="relative">
            <div className="absolute -bottom-3 -right-3 w-full h-full rounded-sm" style={{ background: m.color }} />
            {photo ? (
              <img src={photo} alt={m.name} style={{ objectPosition: m.image_pos || "top" }} className="relative w-full aspect-[3/4] object-cover rounded-sm" />
            ) : (
              <div className="relative w-full aspect-[3/4] rounded-sm flex items-center justify-center" style={{ background: m.color }}>
                <span className="font-heading text-7xl font-bold text-white/90">{initials(m.name)}</span>
              </div>
            )}
          </div>

          <div className="mt-8 space-y-4">
            {m.fav_color && (
              <div className="flex items-center gap-3 text-brand-ink/70">
                <Palette size={17} style={{ color: m.color }} />
                <span className="text-sm">Color favorito: <span className="font-semibold text-brand-ink">{m.fav_color}</span></span>
              </div>
            )}
            {m.email && (
              <a href={`mailto:${m.email}`} data-testid="equipo-email" className="flex items-center gap-3 text-brand-ink/70 hover:text-brand-magenta transition-colors">
                <Mail size={17} style={{ color: m.color }} />
                <span className="text-sm font-semibold break-all">{m.email}</span>
              </a>
            )}
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }} className="md:col-span-7">
          <span className="inline-block w-16 h-1.5 rounded-sm mb-6" style={{ background: m.color }} />
          <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-brand-ink">{m.name}</h1>
          {m.role && <p className="mt-3 text-lg text-brand-ink/70 font-medium">{m.role}</p>}

          {m.motivation && (
            <div className="mt-8 border-l-4 pl-5 py-1" style={{ borderColor: m.color }}>
              <div className="flex items-center gap-2 text-brand-ink/50 mb-2">
                <Heart size={15} style={{ color: m.color }} />
                <span className="text-xs uppercase tracking-[0.15em] font-semibold">Su motivación</span>
              </div>
              <p className="font-heading text-lg md:text-xl font-medium tracking-tight text-brand-ink leading-snug italic">“{m.motivation}”</p>
            </div>
          )}

          {m.bio?.length > 0 && (
            <div className="mt-8">
              <h2 className="text-sm uppercase tracking-[0.15em] font-semibold text-brand-ink/50">Semblanza</h2>
              <div className="mt-4 space-y-4">
                {m.bio.map((p, i) => (
                  <p key={i} className="text-brand-ink/80 leading-relaxed">{p}</p>
                ))}
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default EquipoDetail;
